"use client";

import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/Button";
import { Toast } from "@/components/ui/Toast";
import { Icon } from "@/components/ui/icons";
import { userService, User } from "@/services/userService";
import { api, getApiBaseUrl } from "@/lib/api/apiClient";
import { getCurrentUser } from "@/services/authService";
import { getToken } from "@/utils/auth";
import { checkPasswordPolicy, getPasswordPolicyErrorMessage, PASSWORD_MIN_LENGTH } from "@/utils/passwordPolicy";

type ToastType = "success" | "error" | "warning" | "info";

export default function PerfilManagement() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<"profile" | "password">("profile");
  const [toast, setToast] = useState<{ message: string; type: ToastType; isOpen: boolean }>({
    message: "",
    type: "info",
    isOpen: false,
  });
  const [userId, setUserId] = useState<string>("");

  // Form states
  const [formData, setFormData] = useState({
    name: "",
    userName: "",
    correo: "",
    region: "",
    puesto: "",
  });

  // Password form states
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Photo upload state
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);

  // getToken importado desde @/utils/auth (fuente de verdad centralizada)

  // Obtener userId del usuario actual
  useEffect(() => {
    const loadUser = async () => {
      const currentUser = getCurrentUser();
      let userIdToUse = '';

      if (currentUser && currentUser.ownerID) {
        userIdToUse = String(currentUser.ownerID);
        // Si tenemos datos en localStorage, usarlos temporalmente mientras cargamos
        if (currentUser.name || currentUser.correo) {
          setFormData({
            name: currentUser.name || "",
            userName: currentUser.userName || "",
            correo: currentUser.correo || "",
            region: currentUser.region || "",
            puesto: currentUser.puesto || "",
          });
        }
      } else {
        // Intentar obtener del token JWT
        const token = getToken();
        if (token) {
          try {
            const payload = JSON.parse(atob(token.split(".")[1]));
            if (payload.sub) {
              userIdToUse = String(payload.sub);
            }
          } catch (e) {
            console.error("Error al decodificar token:", e);
          }
        }
      }

      if (userIdToUse) {
        setUserId(userIdToUse);
        await loadUserData(userIdToUse);
      } else {
        showToast("No se pudo identificar al usuario. Por favor, inicia sesión nuevamente.", "error");
        setLoading(false);
      }
    };

    loadUser();
  }, []);

  const loadUserData = async (id: string) => {
    if (!id) {
      showToast("ID de usuario no disponible", "error");
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      console.log("Cargando datos del usuario con ID:", id);
      const token = getToken();
      console.log("Token disponible:", token ? "Sí" : "No");
      
      const response = await userService.getUser(id);
      console.log("Respuesta del servidor:", response);
      console.log("Status:", response.status);
      console.log("Data:", response.data);
      console.log("Error:", response.error);
      
      if (response.data) {
        setUser(response.data);
        setFormData({
          name: response.data.name || formData.name || "",
          userName: response.data.userName || formData.userName || "",
          correo: response.data.correo || formData.correo || "",
          region: response.data.region || formData.region || "",
          puesto: response.data.puesto || formData.puesto || "",
        });
        // Cargar foto guardada si existe
        if (response.data.foto) {
          setPhotoPreview(response.data.foto);
          // Guardar foto en localStorage para mostrarla en el header
          try {
            const currentUser = getCurrentUser();
            if (currentUser) {
              localStorage.setItem("currentUser", JSON.stringify({
                ...currentUser,
                foto: response.data.foto,
              }));
            }
          } catch {
            // localStorage no disponible
          }
        }
        setLoading(false);
      } else {
        const errorMsg = response.error || "Error al cargar los datos del usuario";
        console.error("Error al cargar usuario:", errorMsg, "Status:", response.status);
        
        // Si es un error 401 o 403, puede ser un problema de autenticación
        if (response.status === 401 || response.status === 403) {
          showToast("Error de autenticación. Por favor, inicia sesión nuevamente.", "error");
        } else if (response.status === 404) {
          showToast("Usuario no encontrado en la base de datos.", "error");
        } else {
          showToast(errorMsg, "error");
        }
        
        // Intentar usar datos de localStorage como fallback
        const currentUser = getCurrentUser();
        if (currentUser && (currentUser.name || currentUser.correo)) {
          console.log("Usando datos de localStorage como fallback");
          setFormData({
            name: currentUser.name || formData.name || "",
            userName: currentUser.userName || formData.userName || "",
            correo: currentUser.correo || formData.correo || "",
            region: currentUser.region || formData.region || "",
            puesto: currentUser.puesto || formData.puesto || "",
          });
          // Crear un objeto User básico desde localStorage
          setUser({
            ownerID: currentUser.ownerID || id,
            userName: currentUser.userName || "",
            name: currentUser.name || "",
            correo: currentUser.correo || "",
            region: currentUser.region,
            puesto: currentUser.puesto,
            acl: currentUser.acl || "",
            scope: "",
            forbidden: [],
          });
        }
        setLoading(false);
      }
    } catch (error) {
      console.error("Excepción al cargar usuario:", error);
      const errorMessage = error instanceof Error ? error.message : "Error de conexión";
      showToast(`Error de conexión: ${errorMessage}. Usando datos locales.`, "warning");
      
      // Usar datos de localStorage como fallback
      const currentUser = getCurrentUser();
      if (currentUser && (currentUser.name || currentUser.correo)) {
        console.log("Usando datos de localStorage como fallback después de excepción");
        setFormData({
          name: currentUser.name || "",
          userName: currentUser.userName || "",
          correo: currentUser.correo || "",
          region: currentUser.region || "",
          puesto: currentUser.puesto || "",
        });
        setUser({
          ownerID: currentUser.ownerID || id,
          userName: currentUser.userName || "",
          name: currentUser.name || "",
          correo: currentUser.correo || "",
          region: currentUser.region,
          puesto: currentUser.puesto,
          acl: currentUser.acl || "",
          scope: "",
          forbidden: [],
        });
      }
      setLoading(false);
    }
  };

  const showToast = (message: string, type: ToastType) => {
    setToast({ message, type, isOpen: true });
    setTimeout(() => setToast({ ...toast, isOpen: false }), 3000);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validar tipo de archivo
      if (!file.type.startsWith("image/")) {
        showToast("Por favor selecciona un archivo de imagen válido", "error");
        return;
      }
      // Validar tamaño (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showToast("La imagen no debe exceder 5MB", "error");
        return;
      }
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateProfileForm = (): boolean => {
    if (!formData.name.trim()) {
      showToast("El nombre es requerido", "error");
      return false;
    }
    if (!formData.userName.trim()) {
      showToast("El nombre de usuario es requerido", "error");
      return false;
    }
    if (!formData.correo.trim()) {
      showToast("El correo electrónico es requerido", "error");
      return false;
    }
    // Validar formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.correo)) {
      showToast("Por favor ingresa un correo electrónico válido", "error");
      return false;
    }
    return true;
  };

  const validatePasswordForm = (): boolean => {
    if (!passwordData.currentPassword) {
      showToast("La contraseña actual es requerida", "error");
      return false;
    }
    if (!passwordData.newPassword) {
      showToast("La nueva contraseña es requerida", "error");
      return false;
    }
    const policyCheck = checkPasswordPolicy(passwordData.newPassword);
    if (!policyCheck.isValid) {
      const errorMsg = getPasswordPolicyErrorMessage(policyCheck);
      showToast(errorMsg || "La contraseña no cumple los requisitos", "error");
      return false;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showToast("Las contraseñas no coinciden", "error");
      return false;
    }
    return true;
  };

  const handleSaveProfile = async () => {
    if (!validateProfileForm()) return;

    setSaving(true);
    try {
      // api.put ya incluye credentials: "include" para enviar cookies automáticamente
      const response = await api.put<User>(
        `/users/${userId}`,
        {
          name: formData.name,
          userName: formData.userName,
          correo: formData.correo,
          region: formData.region || null,
          puesto: formData.puesto || null,
        }
      );

      if (response.data) {
        setUser(response.data);
        showToast("Perfil actualizado exitosamente", "success");
        // Actualizar foto si hay una nueva
        if (photoFile) {
          await handleSavePhoto();
        }
        // Actualizar localStorage
        const currentUser = getCurrentUser();
        if (currentUser) {
          try {
            localStorage.setItem("currentUser", JSON.stringify({
              ...currentUser,
              name: response.data.name,
              correo: response.data.correo,
            }));
          } catch {
            // Safari privado; ignorar — el perfil ya se actualizó en el servidor
          }
        }
      } else {
        showToast(response.error || "Error al actualizar el perfil", "error");
      }
    } catch (error) {
      showToast("Error de conexión al actualizar el perfil", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleSavePhoto = async () => {
    if (!photoFile) return;

    try {
      const formDataPhoto = new FormData();
      formDataPhoto.append("photo", photoFile);

      // credentials: "include" envía cookies automáticamente (auth_token)
      const response = await fetch(
        `${getApiBaseUrl()}/users/${userId}/photo`,
        {
          method: "PUT",
          credentials: "include",
          body: formDataPhoto,
        }
      );

      if (response.ok) {
        showToast("Foto de perfil actualizada exitosamente", "success");
        setPhotoFile(null);
        // Recargar datos del usuario
        await loadUserData(userId);
      } else {
        showToast("Error al actualizar la foto de perfil", "error");
      }
    } catch (error) {
      showToast("Error de conexión al actualizar la foto", "error");
    }
  };

  const handleDeletePhoto = async () => {
    if (!photoPreview) return;

    try {
      const response = await fetch(
        `${getApiBaseUrl()}/users/${userId}/photo`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      if (response.ok) {
        showToast("Foto de perfil eliminada", "success");
        setPhotoPreview(null);
        setPhotoFile(null);
        // Actualizar localStorage
        try {
          const currentUser = getCurrentUser();
          if (currentUser) {
            const { foto, ...userWithoutFoto } = currentUser;
            localStorage.setItem("currentUser", JSON.stringify(userWithoutFoto));
          }
        } catch {
          // localStorage no disponible
        }
      } else {
        showToast("Error al eliminar la foto de perfil", "error");
      }
    } catch (error) {
      showToast("Error de conexión al eliminar la foto", "error");
    }
  };

  const handleChangePassword = async () => {
    if (!validatePasswordForm()) return;

    setSaving(true);
    try {
      // Codificar contraseñas para ocultar en Network (el backend las decodifica)
      const encodePassword = (password: string): string => {
        return btoa(unescape(encodeURIComponent(password)));
      };

      const API_BASE_URL = getApiBaseUrl();
      // credentials: "include" envía cookies automáticamente (auth_token)
      const response = await fetch(`${API_BASE_URL}/users/${userId}/password`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          currentPassword: encodePassword(passwordData.currentPassword),
          newPassword: encodePassword(passwordData.newPassword),
        }),
      });

      if (response.ok) {
        showToast("Contraseña actualizada exitosamente", "success");
        setPasswordData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        showToast(errorData.detail || "Error al actualizar la contraseña", "error");
      }
    } catch (error) {
      showToast("Error de conexión al actualizar la contraseña", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="w-full max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-lg p-8">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Mi Perfil</h1>
            <p className="text-sm text-gray-500 mt-1">Administra tu información personal y configuración de cuenta</p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0196A1]"></div>
              <span className="ml-3 text-gray-600">Cargando perfil...</span>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Tabs */}
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                  <button
                    onClick={() => setActiveTab("profile")}
                    className={`${
                      activeTab === "profile"
                        ? "border-[#0196A1] text-[#0196A1]"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    Información Personal
                  </button>
                  <button
                    onClick={() => setActiveTab("password")}
                    className={`${
                      activeTab === "password"
                        ? "border-[#0196A1] text-[#0196A1]"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    Cambiar Contraseña
                  </button>
                </nav>
              </div>

              {/* Profile Tab */}
              {activeTab === "profile" && (
                <div className="space-y-6">
                  {/* Foto de perfil */}
                  <div className="flex items-center space-x-6">
                    <div className="flex-shrink-0">
                      <div className="relative">
                        {photoPreview || user?.correo ? (
                          <div className="h-24 w-24 rounded-full bg-gradient-to-br from-[#0196A1] to-[#017a85] flex items-center justify-center text-white text-2xl font-bold">
                            {photoPreview ? (
                              <img
                                src={photoPreview}
                                alt="Preview"
                                className="h-24 w-24 rounded-full object-cover"
                              />
                            ) : (
                              (user?.name || user?.correo || "U")[0].toUpperCase()
                            )}
                          </div>
                        ) : (
                          <div className="h-24 w-24 rounded-full bg-gray-200 flex items-center justify-center">
                            <Icon name="user" size={48} />
                          </div>
                        )}
                        <label
                          htmlFor="photo-upload"
                          className="absolute bottom-0 right-0 bg-[#0196A1] text-white rounded-full p-2 cursor-pointer hover:bg-[#017a85] transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <input
                            id="photo-upload"
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handlePhotoChange}
                          />
                        </label>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">Foto de Perfil</h3>
                      <p className="text-sm text-gray-500">
                        JPG, PNG o GIF. Máximo 5MB.
                      </p>
                      {photoPreview && (
                        <button
                          type="button"
                          onClick={handleDeletePhoto}
                          className="mt-2 text-sm text-red-600 hover:text-red-800 flex items-center gap-1"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Eliminar foto
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Formulario */}
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                        Nombre Completo <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0196A1] focus:border-[#0196A1]"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="userName" className="block text-sm font-medium text-gray-700 mb-1">
                        Nombre de Usuario <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="userName"
                        name="userName"
                        value={formData.userName}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0196A1] focus:border-[#0196A1]"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="correo" className="block text-sm font-medium text-gray-700 mb-1">
                        Correo Electrónico <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="correo"
                        name="correo"
                        value={formData.correo}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0196A1] focus:border-[#0196A1]"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="region" className="block text-sm font-medium text-gray-700 mb-1">
                        Región
                      </label>
                      <input
                        type="text"
                        id="region"
                        name="region"
                        value={formData.region}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0196A1] focus:border-[#0196A1]"
                      />
                    </div>

                    <div>
                      <label htmlFor="puesto" className="block text-sm font-medium text-gray-700 mb-1">
                        Puesto
                      </label>
                      <input
                        type="text"
                        id="puesto"
                        name="puesto"
                        value={formData.puesto}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0196A1] focus:border-[#0196A1]"
                      />
                    </div>

                    {user && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Rol (ACL)
                        </label>
                        <input
                          type="text"
                          value={user.acl}
                          disabled
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                    <Button
                      onClick={handleSaveProfile}
                      disabled={saving}
                      className="bg-[#0196A1] hover:bg-[#017a85] text-white"
                    >
                      {saving ? "Guardando..." : "Guardar Cambios"}
                    </Button>
                  </div>
                </div>
              )}

              {/* Password Tab */}
              {activeTab === "password" && (
                <div className="space-y-6">
                  <div className="space-y-4">
                    <div>
                      <label
                        htmlFor="currentPassword"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Contraseña Actual <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="password"
                        id="currentPassword"
                        name="currentPassword"
                        value={passwordData.currentPassword}
                        onChange={handlePasswordChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0196A1] focus:border-[#0196A1]"
                        required
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="newPassword"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Nueva Contraseña <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="password"
                        id="newPassword"
                        name="newPassword"
                        value={passwordData.newPassword}
                        onChange={handlePasswordChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0196A1] focus:border-[#0196A1]"
                        required
                      />
                      {/* Indicadores de requisitos de contraseña */}
                      {(() => {
                        const policy = checkPasswordPolicy(passwordData.newPassword);
                        return (
                          <div className="mt-2 space-y-1">
                            <p className="text-xs text-gray-500 mb-1">Requisitos de la contraseña:</p>
                            <div className="flex items-center gap-2">
                              <span className={`text-sm ${policy.minLength ? 'text-green-600' : 'text-gray-400'}`}>
                                {policy.minLength ? '✓' : '○'}
                              </span>
                              <span className={`text-xs ${policy.minLength ? 'text-green-600' : 'text-gray-500'}`}>
                                Mínimo {PASSWORD_MIN_LENGTH} caracteres
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`text-sm ${policy.hasLetter ? 'text-green-600' : 'text-gray-400'}`}>
                                {policy.hasLetter ? '✓' : '○'}
                              </span>
                              <span className={`text-xs ${policy.hasLetter ? 'text-green-600' : 'text-gray-500'}`}>
                                Al menos una letra
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`text-sm ${policy.hasNumber ? 'text-green-600' : 'text-gray-400'}`}>
                                {policy.hasNumber ? '✓' : '○'}
                              </span>
                              <span className={`text-xs ${policy.hasNumber ? 'text-green-600' : 'text-gray-500'}`}>
                                Al menos un número
                              </span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    <div>
                      <label
                        htmlFor="confirmPassword"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Confirmar Nueva Contraseña <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="password"
                        id="confirmPassword"
                        name="confirmPassword"
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0196A1] focus:border-[#0196A1]"
                        required
                      />
                      {passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword && (
                        <p className="mt-1 text-xs text-red-600">Las contraseñas no coinciden</p>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                    <Button
                      onClick={handleChangePassword}
                      disabled={saving || !checkPasswordPolicy(passwordData.newPassword).isValid || passwordData.newPassword !== passwordData.confirmPassword}
                      className="bg-[#0196A1] hover:bg-[#017a85] text-white"
                    >
                      {saving ? "Actualizando..." : "Cambiar Contraseña"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <Toast
        message={toast.message}
        type={toast.type}
        isOpen={toast.isOpen}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </>
  );
}
